import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';

interface Ticket {
  id: string;
  ticketNumber: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  category?: string;
  agentName?: string;
  hasServiceWorkflow?: boolean;
  serviceWorkflows?: ServiceWorkflow[];
}

interface ServiceWorkflow {
  workflowId: string;
  workflowNumber: string;
  currentStep: number;
  status: string;
  stepDetails?: WorkflowStep[];
}

interface WorkflowStep {
  stepNumber: number;
  stepName: string;
  status: string;
  completedAt?: string;
}

interface CustomerTicketHistoryProps {
  customerId: string;
  customerData?: any;
}

const CustomerTicketHistory: React.FC<CustomerTicketHistoryProps> = ({ 
  customerId, 
  customerData 
}) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'status'>('newest');

  useEffect(() => {
    loadTickets();
  }, [customerId]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get customer tickets - we'll need to create this API endpoint
      const response = await apiService.getCustomerTickets(customerId);
      console.log('🎫 Customer tickets response:', response);
      console.log('🎫 Number of tickets:', response.tickets?.length || 0);
      
      // Debug workflow information
      if (response.tickets) {
        response.tickets.forEach((ticket: any, index: number) => {
          console.log(`🎫 Ticket ${index + 1}:`, {
            id: ticket.id,
            title: ticket.title,
            hasServiceWorkflow: ticket.hasServiceWorkflow,
            serviceWorkflows: ticket.serviceWorkflows
          });
        });
      }
      
      setTickets(response.tickets || []);
    } catch (err: any) {
      console.error('Error loading tickets:', err);
      setError('Failed to load ticket history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'new':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-400';
      case 'in_progress':
      case 'in_progress_ai_agent':
      case 'in_progress_tier1':
      case 'in_progress_tier2':
      case 'in_progress_tier3':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400';
      case 'closed-resolved':
      case 'closed-by-agent-resolved':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400';
      case 'closed-not-resolved':
      case 'closed-partially-resolved':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-400';
      case 'escalated-awaiting-agent':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400';
      case 'abandoned':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-400';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400';
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatStatus = (status: string) => {
    return status
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const filteredAndSortedTickets = () => {
    let filtered = tickets;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = tickets.filter(ticket => {
        if (statusFilter === 'open') {
          return !ticket.status.toLowerCase().includes('closed') && 
                 ticket.status.toLowerCase() !== 'abandoned';
        }
        if (statusFilter === 'closed') {
          return ticket.status.toLowerCase().includes('closed') || 
                 ticket.status.toLowerCase() === 'abandoned';
        }
        return ticket.status.toLowerCase().includes(statusFilter.toLowerCase());
      });
    }

    // Sort tickets
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'status':
          return a.status.localeCompare(b.status);
        case 'newest':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading tickets...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">
        <div className="flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
        <button
          onClick={loadTickets}
          className="mt-2 text-sm underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const displayTickets = filteredAndSortedTickets();

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header and Filters */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Support Ticket History
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {tickets.length} ticket{tickets.length !== 1 ? 's' : ''} total
              </p>
            </div>
            
            <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              {/* Status Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Tickets</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="in_progress">In Progress</option>
                </select>
              </div>
              
              {/* Sort By */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest' | 'status')}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="status">By Status</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tickets List */}
      {displayTickets.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No tickets found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {statusFilter === 'all' 
              ? "You haven't created any support tickets yet." 
              : `No tickets found with the selected filter: ${statusFilter}`
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {displayTickets.map((ticket) => (
            <div
              key={ticket.id}
              className="bg-white dark:bg-gray-800 shadow rounded-lg hover:shadow-md transition-shadow duration-200"
            >
              <div className="px-6 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        #{ticket.ticketNumber}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                        {formatStatus(ticket.status)}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                      </span>
                    </div>
                    
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {ticket.title}
                    </h4>
                    
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                      {ticket.description}
                    </p>
                    
                    {/* Debug: Show workflow data */}
                    {process.env.NODE_ENV === 'development' && (
                      <div className="mb-2 p-2 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                        <strong>Debug:</strong> hasServiceWorkflow: {String(ticket.hasServiceWorkflow)}, 
                        workflows: {JSON.stringify(ticket.serviceWorkflows)}
                      </div>
                    )}
                    
                    {/* Service Workflow Display */}
                    {ticket.hasServiceWorkflow && ticket.serviceWorkflows && ticket.serviceWorkflows.length > 0 && (
                      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div className="flex items-center mb-2">
                          <svg className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 00-2 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                          </svg>
                          <h5 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                            Service Workflow
                          </h5>
                        </div>
                        {ticket.serviceWorkflows.map((workflow) => (
                          <div key={workflow.workflowId} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-blue-800 dark:text-blue-200">
                                #{workflow.workflowNumber}
                              </span>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                workflow.status === 'completed' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                                  : workflow.status === 'in_progress'
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400'
                                  : workflow.status === 'cancelled'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                                {workflow.status}
                              </span>
                            </div>
                            
                            {/* Workflow Steps Progress */}
                            {workflow.stepDetails && workflow.stepDetails.length > 0 && (
                              <div className="mt-2">
                                <div className="flex items-center space-x-1">
                                  {workflow.stepDetails.map((step, index) => (
                                    <React.Fragment key={step.stepNumber}>
                                      <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                                        step.status === 'completed'
                                          ? 'bg-green-500 text-white'
                                          : step.stepNumber === workflow.currentStep
                                          ? 'bg-blue-500 text-white'
                                          : step.stepNumber < workflow.currentStep
                                          ? 'bg-green-500 text-white'
                                          : 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                                      }`}>
                                        {step.status === 'completed' ? (
                                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                        ) : (
                                          step.stepNumber
                                        )}
                                      </div>
                                      {index < (workflow.stepDetails?.length || 0) - 1 && (
                                        <div className={`flex-1 h-0.5 ${
                                          step.status === 'completed' || step.stepNumber < workflow.currentStep
                                            ? 'bg-green-500'
                                            : 'bg-gray-300 dark:bg-gray-600'
                                        }`} />
                                      )}
                                    </React.Fragment>
                                  ))}
                                </div>
                                <div className="mt-1 text-xs text-blue-700 dark:text-blue-300">
                                  Step {workflow.currentStep} of {workflow.stepDetails?.length || 0}: {
                                    workflow.stepDetails?.find(s => s.stepNumber === workflow.currentStep)?.stepName || 'Unknown'
                                  }
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex flex-wrap items-center text-xs text-gray-500 dark:text-gray-400 space-x-4">
                      <span>Created: {new Date(ticket.createdAt).toLocaleDateString()}</span>
                      <span>Updated: {new Date(ticket.updatedAt).toLocaleDateString()}</span>
                      {ticket.resolvedAt && (
                        <span>Resolved: {new Date(ticket.resolvedAt).toLocaleDateString()}</span>
                      )}
                      {ticket.agentName && (
                        <span>Agent: {ticket.agentName}</span>
                      )}
                      {ticket.category && (
                        <span>Category: {ticket.category}</span>
                      )}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setSelectedTicket(selectedTicket?.id === ticket.id ? null : ticket)}
                    className="ml-4 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                  >
                    {selectedTicket?.id === ticket.id ? 'Hide Details' : 'View Details'}
                  </button>
                </div>
                
                {/* Expanded Details */}
                {selectedTicket?.id === ticket.id && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                        Full Description
                      </h5>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {ticket.description}
                      </p>
                      
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">Status:</span>
                          <p className="text-gray-600 dark:text-gray-400">{formatStatus(ticket.status)}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">Priority:</span>
                          <p className="text-gray-600 dark:text-gray-400">{ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}</p>
                        </div>
                        {ticket.category && (
                          <div>
                            <span className="font-medium text-gray-900 dark:text-gray-100">Category:</span>
                            <p className="text-gray-600 dark:text-gray-400">{ticket.category}</p>
                          </div>
                        )}
                        {ticket.agentName && (
                          <div>
                            <span className="font-medium text-gray-900 dark:text-gray-100">Assigned Agent:</span>
                            <p className="text-gray-600 dark:text-gray-400">{ticket.agentName}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerTicketHistory;
